/**
 * Processor KPI Cards Component
 * Displays key performance indicators for processor dashboards.
 * 
 * Extracted from App.js for better code organization.
 */

import React from 'react';
import { FileText, Clock, TrendingUp, Mail, AlertCircle, CheckCircle, RotateCcw } from 'lucide-react';
import { KpiPillRow } from '../dashboard/KpiPillRow';

/**
 * KPI Card Component - Reusable stat card
 */
const KPICard = ({ icon: Icon, label, value, loading, ariaLabel }) => (
  <div 
    className="kpi-card"
    role="group"
    tabIndex={0}
    aria-label={ariaLabel || (loading ? `${label}: loading` : `${label}: ${value}`)}
  >
    <div className="kpi-card-accent" />
    <div className="kpi-card-icon">
      <Icon className="h-5 w-5" aria-hidden="true" />
    </div>
    <div className="kpi-card-body">
      <div className="kpi-card-label">{label}</div>
      <div className="kpi-card-value">
        {loading ? (
          <span
            className="inline-block h-6 w-10 rounded-md bg-slate-200 animate-pulse align-middle"
            data-testid="kpi-value-skeleton"
            aria-hidden="true"
          />
        ) : value}
      </div>
    </div>
  </div>
);

/**
 * Processor KPI Cards Component
 * 
 * Props:
 * - stats: Object containing processor statistics
 * - userRole: User's role to filter cards (search roles hide Mail-Away cards)
 */
const ProcessorKPICards = ({ stats, userRole, loading }) => {
  // Roles that should NOT see Mail-Away related KPI cards
  const searchRoles = ['search_processor', 'title_search_team_leader'];
  const isSearchRole = searchRoles.includes(userRole);

  const kpiConfig = [
    {
      icon: FileText,
      label: 'Total Orders',
      value: stats?.total_orders || 0,
      key: 'total'
    },
    {
      icon: Clock,
      label: 'Open Orders',
      value: stats?.open_orders || 0,
      key: 'open'
    },
    {
      icon: TrendingUp,
      label: 'Pending',
      value: stats?.in_progress || 0,
      key: 'pending'
    },
    {
      icon: Mail,
      label: 'Mail-Away',
      value: stats?.mail_away || 0,
      key: 'mailaway'
    },
    {
      icon: Mail,
      label: 'Mail-Away Received',
      value: stats?.mail_away_received || 0,
      key: 'mailaway_received'
    },
    {
      icon: AlertCircle,
      label: 'Clarification',
      value: stats?.clarification || 0,
      key: 'clarification'
    },
    {
      icon: CheckCircle,
      label: 'Clarification Received',
      value: stats?.clarification_received || 0,
      key: 'clarification_received'
    },
    // Feb 2026 — QC Workflow Phase 1: dedicated KPI so processors see
    // soft-returned orders distinctly from regular Pending. DB status is
    // still `pending_correction`; user-facing label is "Correction Required".
    {
      icon: RotateCcw,
      label: 'Correction Required',
      value: stats?.returned_for_correction || 0,
      key: 'returned_for_correction'
    }
  ];

  // Filter out Mail-Away cards for search roles
  const filteredKpiConfig = isSearchRole
    ? kpiConfig.filter(kpi => !['mailaway', 'mailaway_received'].includes(kpi.key))
    : kpiConfig;

  // KPI-PILL-001: single-row pill (Tax Search + Title Search Processor dashboards)
  return (
    <KpiPillRow items={filteredKpiConfig} loading={loading} testId="processor-kpi-cards" />
  );
};

export { ProcessorKPICards, KPICard };
export default ProcessorKPICards;
